import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_AD, GET_FEATURED_ADS, GET_CATEGORY_ADS } from '../graphql/queries';
import { TOGGLE_FAVORITE, CONTACT_AD } from '../graphql/mutations';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { getAbsoluteImageUrl } from '../lib/imageUtils';
import {
  HeartIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ShareIcon,
  FlagIcon,
  EyeIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import {
  Card,
  Button,
  Form,
  Alert,
  Modal,
  Row,
  Col,
  Badge,
  Container
} from 'react-bootstrap';

const AdDetail: React.FC = () => {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  let slug = rawSlug || '';

  try {
    if (slug.includes('%')) {
      slug = decodeURIComponent(slug);
    }
  } catch (e) {
    // If decoding fails, use as-is
  }

  const { isAuthenticated } = useAuth();
  const [showContactForm, setShowContactForm] = useState(false);
  const [showChatBox, setShowChatBox] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{text: string, isUser: boolean}>>([]);

  const { data, loading, error } = useQuery(GET_AD, {
    variables: { slug },
    skip: !slug,
  });

  const { data: featuredData } = useQuery(GET_FEATURED_ADS, {
    variables: { limit: 5 },
  });

  // Get related ads from the same category
  const categorySlug = data?.adBySlug?.category?.slug;
  const { data: relatedData } = useQuery(GET_CATEGORY_ADS, {
    variables: { categorySlug, limit: 5 },
    skip: !categorySlug,
  });

  const [toggleFavorite] = useMutation(TOGGLE_FAVORITE);
  const [contactAd] = useMutation(CONTACT_AD);

  if (loading) {
    return (
      <Layout showSidebar={false}>
        <Container className="py-5 text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout showSidebar={false}>
        <Container className="py-5 text-center">
          <Alert variant="danger">
            <Alert.Heading as="h4">Error Loading Ad</Alert.Heading>
            <p className="mb-2">There was a problem loading this ad.</p>
            <p className="small mb-0">{error.message}</p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  if (!data?.adBySlug) {
    return (
      <Layout showSidebar={false}>
        <Container className="py-5 text-center">
          <h1 className="display-6 fw-bold mb-3">Ad Not Found</h1>
          <p className="text-muted">The ad you're looking for doesn't exist or has been removed.</p>
        </Container>
      </Layout>
    );
  }

  const ad = data.adBySlug;
  const primaryImage = ad.imagesList?.find((img: any) => img.isPrimary) || ad.imagesList?.[0];
  const featuredAds = featuredData?.featuredAds || [];
  const relatedAds = relatedData?.categoryAds?.filter((a: any) => a.id !== ad.id) || [];

  const formatPrice = (price: string | null | undefined) => {
    if (!price) return null;
    const numPrice = parseFloat(price);
    if (!isNaN(numPrice)) {
      return Math.round(numPrice).toLocaleString('en-NP');
    }
    return price;
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      alert('Please login to add favorites');
      return;
    }
    try {
      await toggleFavorite({ variables: { adId: ad.id } });
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await contactAd({
        variables: {
          input: {
            adId: ad.id,
            name: '',
            email: '',
            message: contactMessage,
          }
        }
      });
      setShowContactForm(false);
      setContactMessage('');
      alert('Message sent successfully!');
    } catch (error) {
      alert('Failed to send message');
    }
  };

  const handleSendChat = () => {
    if (!chatMessage.trim()) return;
    setChatMessages([...chatMessages, { text: chatMessage, isUser: true }]);
    setChatMessage('');
    // Simulate seller response
    setTimeout(() => {
      setChatMessages(prev => [...prev, { text: 'Thanks for your message! The seller will respond soon.', isUser: false }]);
    }, 1000);
  };

  const getConditionBadgeVariant = (condition: string) => {
    switch (condition) {
      case 'new': return 'success';
      case 'like_new': return 'success';
      default: return 'secondary';
    }
  };

  return (
    <Layout showSidebar={false}>
      <div className="pw-page">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/" className="text-decoration-none">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/category/${categorySlug}`} className="text-decoration-none">{ad.categoryPath}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">{ad.title}</li>
        </ol>
      </nav>

      <Row>
        {/* Left Column - Main Content (3/4 width) */}
        <Col lg={9} className="space-3">
          {/* Header Section - ID, Title, Price, Stats */}
          <Card className="mb-4 pw-card">
            <Card.Body>
              <Row className="align-items-start">
                <Col>
                  {/* Ad ID */}
                  <div className="text-muted small mb-2">
                    Ad ID: <span className="font-monospace fw-medium">{ad.id}</span>
                  </div>

                  {/* Title */}
                  <h1 className="display-6 fw-bold mb-3">{ad.title}</h1>

                  {/* Price */}
                  <div className="d-flex align-items-baseline gap-3 mb-4">
                    <span className="text-primary fw-bold" style={{ fontSize: '2.5rem' }}>
                      {ad.priceType === 'free' ? 'Free' :
                       ad.priceType === 'swap' ? 'Swap/Trade' :
                       ad.price ? `Rs ${formatPrice(ad.price)}` : 'Contact for Price'}
                    </span>
                    {ad.priceType === 'negotiable' && (
                      <span className="text-muted small">(Negotiable)</span>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="d-flex flex-wrap gap-4 text-muted">
                    <span className="d-flex align-items-center">
                      <EyeIcon style={{ width: '1rem', height: '1rem', marginRight: '0.25rem' }} />
                      {ad.viewCount} views
                    </span>
                    <span className="d-flex align-items-center">
                      <CalendarIcon style={{ width: '1rem', height: '1rem', marginRight: '0.25rem' }} />
                      {ad.publishedAt && formatDistanceToNow(new Date(ad.publishedAt), { addSuffix: true })}
                    </span>
                    <Badge bg={getConditionBadgeVariant(ad.condition)}>
                      {ad.condition?.replace('_', ' ')}
                    </Badge>
                  </div>
                </Col>

                {/* Action Buttons */}
                <Col xs="auto" className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={handleFavorite}
                    title="Add to favorites"
                  >
                    {ad.isFavorited ? (
                      <HeartIconSolid style={{ width: '1.25rem', height: '1.25rem', color: '#dc3545' }} />
                    ) : (
                      <HeartIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                    )}
                  </Button>
                  <Button variant="outline-secondary" title="Share">
                    <ShareIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                  </Button>
                  <Button variant="outline-secondary" title="Report">
                    <FlagIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Image Gallery */}
          <Card className="mb-4 pw-card overflow-hidden">
            <div style={{ aspectRatio: '16 / 9', backgroundColor: '#f8f9fa' }}>
              {primaryImage ? (
                <img
                  src={getAbsoluteImageUrl(primaryImage.url) || '/placeholder-ad.png'}
                  alt={ad.title}
                  className="w-100 h-100 object-fit-cover"
                />
              ) : (
                <div className="w-100 h-100 d-flex align-items-center justify-center text-muted">
                  No image available
                </div>
              )}
            </div>
            {ad.imagesList?.length > 1 && (
              <Card.Body className="d-flex gap-2 overflow-x-auto p-3">
                {ad.imagesList.map((img: any) => (
                  <img
                    key={img.id}
                    src={getAbsoluteImageUrl(img.url) || '/placeholder-ad.png'}
                    alt=""
                    style={{ width: '5rem', height: '5rem', objectFit: 'cover' }}
                    className="rounded cursor-pointer"
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  />
                ))}
              </Card.Body>
            )}
          </Card>

          {/* Description */}
          <Card className="mb-4 pw-card">
            <Card.Body>
              <h2 className="h4 fw-bold mb-3">Description</h2>
              <p className="text-secondary" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.75' }}>
                {ad.description || 'No description provided.'}
              </p>
            </Card.Body>
          </Card>

          {/* Details */}
          {(ad.brand || ad.modelName || ad.year) && (
            <Card className="mb-4 pw-card">
              <Card.Body>
                <h2 className="h4 fw-bold mb-4">Details</h2>
                <Row>
                  {ad.brand && (
                    <Col md={6} lg={4} className="mb-3">
                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted">Brand</span>
                        <span className="fw-medium">{ad.brand}</span>
                      </div>
                    </Col>
                  )}
                  {ad.modelName && (
                    <Col md={6} lg={4} className="mb-3">
                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted">Model</span>
                        <span className="fw-medium">{ad.modelName}</span>
                      </div>
                    </Col>
                  )}
                  {ad.year && (
                    <Col md={6} lg={4} className="mb-3">
                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted">Year</span>
                        <span className="fw-medium">{ad.year}</span>
                      </div>
                    </Col>
                  )}
                  {ad.condition && (
                    <Col md={6} lg={4} className="mb-3">
                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted">Condition</span>
                        <span className="fw-medium text-capitalize">{ad.condition.replace('_', ' ')}</span>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Location */}
          <Card className="mb-4 pw-card">
            <Card.Body>
              <h2 className="h4 fw-bold mb-4">Location</h2>
              <div className="d-flex align-items-start text-secondary">
                <MapPinIcon style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.75rem', flexShrink: 0 }} />
                <div>
                  <div className="fs-5 fw-medium">{ad.location}</div>
                  <div>{ad.city}</div>
                  {ad.state && <div>{ad.state}</div>}
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Contact Information */}
          <Card className="mb-4 pw-card">
            <Card.Body>
              <h2 className="h4 fw-bold mb-4">Contact Information</h2>
              <div className="d-flex flex-column gap-3">
                {ad.contactPhone && (
                  <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                    <div className="d-flex align-items-center">
                      <PhoneIcon className="text-primary me-3" style={{ width: '1.25rem', height: '1.25rem' }} />
                      <div>
                        <div className="small text-muted">Phone</div>
                        <div className="fw-medium">{ad.contactPhone}</div>
                      </div>
                    </div>
                    <a href={`tel:${ad.contactPhone}`} className="btn btn-primary btn-sm">
                      Call Now
                    </a>
                  </div>
                )}
                {ad.contactWhatsapp && (
                  <div className="d-flex align-items-center justify-content-between p-3 bg-success bg-opacity-10 rounded">
                    <div className="d-flex align-items-center">
                      <ChatBubbleLeftRightIcon className="text-success me-3" style={{ width: '1.25rem', height: '1.25rem' }} />
                      <div>
                        <div className="small text-muted">WhatsApp</div>
                        <div className="fw-medium">{ad.contactWhatsapp}</div>
                      </div>
                    </div>
                    <a
                      href={`https://wa.me/${ad.contactWhatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-success btn-sm"
                    >
                      Chat
                    </a>
                  </div>
                )}
                {ad.contactEmail && (
                  <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                    <div className="d-flex align-items-center">
                      <EnvelopeIcon className="text-primary me-3" style={{ width: '1.25rem', height: '1.25rem' }} />
                      <div>
                        <div className="small text-muted">Email</div>
                        <div className="fw-medium">{ad.contactEmail}</div>
                      </div>
                    </div>
                    <a href={`mailto:${ad.contactEmail}`} className="btn btn-primary btn-sm">
                      Send Email
                    </a>
                  </div>
                )}
                <Button
                  variant="outline-primary"
                  size="lg"
                  className="w-100 d-flex align-items-center justify-content-center gap-2"
                  onClick={() => setShowContactForm(true)}
                >
                  <EnvelopeIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                  Send Message to Seller
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Seller Information */}
          <Card className="mb-4 pw-card">
            <Card.Body>
              <h2 className="h4 fw-bold mb-4">Seller Information</h2>
              <Link to={`/profile/${ad.user?.username}`} className="text-decoration-none">
                <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded hover-bg-secondary transition-colors">
                  <div className="d-flex align-items-center">
                    <img
                      src={getAbsoluteImageUrl(ad.user?.profilePicture) || '/default-avatar.png'}
                      alt={ad.user?.displayName}
                      style={{ width: '4rem', height: '4rem' }}
                      className="rounded-circle object-fit-cover me-3"
                    />
                    <div>
                      <div className="fw-semibold fs-5 d-flex align-items-center gap-2">
                        {ad.user?.displayName}
                        {ad.user?.isVerified && (
                          <span className="text-success" title="Verified">✓</span>
                        )}
                      </div>
                      <div className="small text-muted">@{ad.user?.username}</div>
                      <div className="small text-muted mt-1">
                        Member since {ad.user?.dateJoined ? new Date(ad.user.dateJoined).getFullYear() : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <span className="text-primary small fw-medium">View Profile →</span>
                </div>
              </Link>
            </Card.Body>
          </Card>

          {/* Safety Tips */}
          <Alert variant="warning" className="mb-4">
            <Alert.Heading as="h5" className="fw-bold">⚠️ Safety Tips</Alert.Heading>
            <ul className="small mb-0">
              <li>Meet in a safe, public place with people around</li>
              <li>Inspect the item thoroughly before making payment</li>
              <li>Never pay in advance without seeing the item</li>
              <li>Beware of deals that seem too good to be true</li>
              <li>Trust your instincts - if something feels wrong, walk away</li>
            </ul>
          </Alert>
        </Col>

        {/* Right Column - Sidebar (1/4 width) */}
        <Col lg={3}>
          {/* Boosted/Featured Ads */}
          {featuredAds.length > 0 && (
            <Card className="mb-4 pw-card position-sticky" style={{ top: '1rem' }}>
              <Card.Body>
                <div className="d-flex align-items-center gap-2 mb-3 pb-3 border-bottom">
                  <BoltIcon style={{ width: '1.25rem', height: '1.25rem', color: '#ffc107' }} />
                  <h3 className="fw-bold mb-0">Sponsored Ads</h3>
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {featuredAds.slice(0, 5).map((featuredAd: any) => (
                    <Link key={featuredAd.id} to={`/ad/${featuredAd.slug}`} className="text-decoration-none">
                      <div className="d-flex gap-3 p-2 rounded hover-bg-light transition-colors mb-3">
                        <img
                          src={getAbsoluteImageUrl(featuredAd.primaryImage) || '/placeholder-ad.png'}
                          alt={featuredAd.title}
                          style={{ width: '5rem', height: '5rem', objectFit: 'cover' }}
                          className="rounded flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h6 className="fw-medium text-dark text-truncate mb-1" style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {featuredAd.title}
                          </h6>
                          <div className="text-primary fw-bold small mb-1">
                            Rs {formatPrice(featuredAd.price)}
                          </div>
                          <div className="text-muted small d-flex align-items-center">
                            <MapPinIcon style={{ width: '0.75rem', height: '0.75rem', marginRight: '0.25rem' }} />
                            {featuredAd.city}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Related Products */}
          {relatedAds.length > 0 && (
            <Card className="mb-4 pw-card">
              <Card.Body>
                <h3 className="fw-bold mb-4 pb-3 border-bottom">Related Products</h3>
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {relatedAds.slice(0, 5).map((relatedAd: any) => (
                    <Link key={relatedAd.id} to={`/ad/${relatedAd.slug}`} className="text-decoration-none">
                      <div className="d-flex gap-3 p-2 rounded hover-bg-light transition-colors mb-3">
                        <img
                          src={getAbsoluteImageUrl(relatedAd.primaryImage) || '/placeholder-ad.png'}
                          alt={relatedAd.title}
                          style={{ width: '5rem', height: '5rem', objectFit: 'cover' }}
                          className="rounded flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h6 className="fw-medium text-dark text-truncate mb-1" style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {relatedAd.title}
                          </h6>
                          <div className="text-primary fw-bold small mb-1">
                            Rs {formatPrice(relatedAd.price)}
                          </div>
                          <div className="text-muted small d-flex align-items-center">
                            <MapPinIcon style={{ width: '0.75rem', height: '0.75rem', marginRight: '0.25rem' }} />
                            {relatedAd.city}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Floating Chat Button */}
      <Button
        variant="primary"
        className="position-fixed bottom-0 end-0 m-4 rounded-circle"
        style={{ width: '3.5rem', height: '3.5rem', zIndex: 40 }}
        onClick={() => setShowChatBox(true)}
        title="Chat with us"
      >
        <ChatBubbleLeftRightIcon style={{ width: '1.5rem', height: '1.5rem' }} />
      </Button>

      {/* Chat Box */}
      {showChatBox && (
        <div className="position-fixed bottom-0 end-0 bg-white rounded shadow-lg overflow-hidden" style={{
          bottom: '6rem',
          right: '1.5rem',
          width: '20rem',
          zIndex: 50
        }}>
          <div className="bg-primary text-white p-3 d-flex align-items-center justify-content-between">
            <div>
              <h3 className="fs-6 fw-semibold mb-0">Customer Support</h3>
              <p className="small mb-0">We typically reply in minutes</p>
            </div>
            <Button
              variant="link"
              className="text-white p-0"
              onClick={() => setShowChatBox(false)}
            >
              <XMarkIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            </Button>
          </div>

          <div style={{ height: '20rem', overflowY: 'auto' }} className="p-3 bg-light space-3">
            {chatMessages.length === 0 ? (
              <div className="text-center text-muted small py-4">
                <ChatBubbleLeftRightIcon style={{ width: '2.5rem', height: '2.5rem', margin: '0 auto 0.5rem' }} />
                <p>Start a conversation with us</p>
              </div>
            ) : (
              chatMessages.map((msg, idx) => (
                <div key={idx} className={`d-flex ${msg.isUser ? 'justify-content-end' : 'justify-content-start'} mb-3`}>
                  <div className={`p-2 rounded ${msg.isUser ? 'bg-primary text-white' : 'bg-white text-dark shadow-sm'}`} style={{ maxWidth: '80%' }}>
                    <p className="small mb-0">{msg.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-3 border-top">
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
                placeholder="Type your message..."
                size="sm"
              />
              <Button
                variant="primary"
                onClick={handleSendChat}
                size="sm"
              >
                <ChatBubbleLeftRightIcon style={{ width: '1.25rem', height: '1.25rem' }} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      <Modal show={showContactForm} onHide={() => setShowContactForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Contact Seller</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleContactSubmit}>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder="Write your message..."
                rows={4}
                required
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button variant="primary" type="submit" className="flex-1">
                Send Message
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowContactForm(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      </div>
    </Layout>
  );
};

export default AdDetail;
